/**
 * BlogManager Component
 * Admin: list blogs from PostgreSQL, add, edit, view, delete.
 * Data: optiprime_main.blogs + blog_translations (en).
 */

import React, { useState, useEffect, useMemo } from 'react';
import './FeedbackManager.css';
import './BlogManager.css';
import RichTextEditor from './RichTextEditor';

const API_BLOG = '/api/blog-manager';

/** Same embed URL logic as public optiprime.ai/blog (see node-onboarding-dashboard server tiktokPublicRow). */
function getTiktokEmbedSrc(video) {
  if (video?.embed_url) return video.embed_url;
  const vid = video?.video_id;
  if (vid == null || String(vid).trim() === '') return '';
  return `https://www.tiktok.com/player/v1/${encodeURIComponent(String(vid))}?loop=1&rel=0&controls=1&music_info=1`;
}

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [viewingBlog, setViewingBlog] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    excerpt: '',
    body: '',
    image_url: '',
    highlight: false,
    status: 'draft',
    scheduled_for: '',
    seo_meta: { metaTitle: '', metaDescription: '', ogImage: '' }
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [tiktokVideos, setTiktokVideos] = useState([]);
  const [tiktokLoading, setTiktokLoading] = useState(true);
  const [tiktokError, setTiktokError] = useState('');
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [tiktokSubmitting, setTiktokSubmitting] = useState(false);

  const fetchBlogs = async () => {
    setLoading(true);
    setError('');
    try {
      const url = statusFilter ? `${API_BLOG}/blogs?status=${statusFilter}` : `${API_BLOG}/blogs`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success && Array.isArray(data.blogs)) {
        setBlogs(data.blogs);
      } else {
        setError(data.error || 'Failed to load blogs');
        setBlogs([]);
      }
    } catch (err) {
      setError(err.message || 'Network error');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [statusFilter]);

  const fetchTiktokVideos = async () => {
    setTiktokLoading(true);
    setTiktokError('');
    try {
      const res = await fetch(`${API_BLOG}/tiktok-videos`);
      const data = await res.json();
      if (data.success && Array.isArray(data.videos)) {
        setTiktokVideos(data.videos);
      } else {
        setTiktokVideos([]);
        setTiktokError(data.error || 'Failed to load TikTok list');
      }
    } catch (err) {
      setTiktokVideos([]);
      setTiktokError(err.message || 'Network error');
    } finally {
      setTiktokLoading(false);
    }
  };

  useEffect(() => {
    fetchTiktokVideos();
  }, []);

  const handleAddTiktok = async (e) => {
    e.preventDefault();
    setTiktokError('');
    const u = tiktokUrl.trim();
    if (!u) {
      setTiktokError('Paste a TikTok video URL or numeric video id.');
      return;
    }
    setTiktokSubmitting(true);
    try {
      const res = await fetch(`${API_BLOG}/tiktok-videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: u })
      });
      const data = await res.json();
      if (data.success) {
        setTiktokUrl('');
        fetchTiktokVideos();
      } else {
        setTiktokError(data.error || 'Add failed');
      }
    } catch (err) {
      setTiktokError(err.message || 'Request failed');
    } finally {
      setTiktokSubmitting(false);
    }
  };

  const handleDeleteTiktok = async (id) => {
    if (!window.confirm('Remove this video from the public blog page?')) return;
    setTiktokError('');
    try {
      const res = await fetch(`${API_BLOG}/tiktok-videos/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        fetchTiktokVideos();
      } else {
        setTiktokError(data.error || 'Delete failed');
      }
    } catch (err) {
      setTiktokError(err.message || 'Delete failed');
    }
  };

  const openAdd = () => {
    setEditingBlog(null);
    setFormData({
      slug: '',
      title: '',
      excerpt: '',
      body: '',
      image_url: '',
      highlight: false,
      status: 'draft',
      scheduled_for: '',
      seo_meta: { metaTitle: '', metaDescription: '', ogImage: '' }
    });
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (blog) => {
    setEditingBlog(blog);
    const seo = blog.seo_meta && typeof blog.seo_meta === 'object'
      ? blog.seo_meta
      : { metaTitle: '', metaDescription: '', ogImage: '' };
    setFormData({
      slug: blog.slug || '',
      title: blog.title || '',
      excerpt: blog.excerpt || '',
      body: blog.body || '',
      image_url: blog.image_url || '',
      highlight: !!blog.highlight,
      status: blog.status || 'draft',
      scheduled_for: blog.scheduled_for ? new Date(blog.scheduled_for).toISOString().slice(0, 16) : '',
      seo_meta: {
        metaTitle: seo.metaTitle || '',
        metaDescription: seo.metaDescription || '',
        ogImage: seo.ogImage || ''
      }
    });
    setFormError('');
    setFormOpen(true);
  };

  const openView = async (blog) => {
    try {
      const res = await fetch(`${API_BLOG}/blogs/${blog.id}`);
      const data = await res.json();
      if (data.success && data.blog) {
        setViewingBlog(data.blog);
        setViewOpen(true);
      } else {
        setViewingBlog(blog);
        setViewOpen(true);
      }
    } catch {
      setViewingBlog(blog);
      setViewOpen(true);
    }
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingBlog(null);
    setFormError('');
  };

  const closeView = () => {
    setViewOpen(false);
    setViewingBlog(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitLoading(true);
    try {
      const payload = {
        slug: formData.slug.trim(),
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim(),
        body: formData.body.trim(),
        image_url: formData.image_url.trim() || null,
        highlight: formData.highlight,
        status: formData.status,
        scheduled_for: formData.status === 'scheduled' && formData.scheduled_for
          ? new Date(formData.scheduled_for).toISOString()
          : null,
        seo_meta: (formData.seo_meta.metaTitle || formData.seo_meta.metaDescription || formData.seo_meta.ogImage)
          ? formData.seo_meta
          : null
      };
      if (editingBlog) {
        const res = await fetch(`${API_BLOG}/blogs/${editingBlog.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.success) {
          fetchBlogs();
          closeForm();
        } else {
          setFormError(data.error || 'Update failed');
        }
      } else {
        const res = await fetch(`${API_BLOG}/blogs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.success) {
          fetchBlogs();
          closeForm();
        } else {
          setFormError(data.error || 'Create failed');
        }
      }
    } catch (err) {
      setFormError(err.message || 'Request failed');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (blog) => {
    if (!window.confirm(`Delete blog "${blog.title || blog.slug}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BLOG}/blogs/${blog.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        fetchBlogs();
      } else {
        alert(data.error || 'Delete failed');
      }
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getPrimaryDate = (blog) => blog.scheduled_for || blog.uploaded_at || blog.created_at || null;

  const dashboardBlogs = useMemo(() => {
    const lowerSearch = searchTerm.trim().toLowerCase();
    const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;

    const filtered = blogs.filter((blog) => {
      if (lowerSearch) {
        const hay = `${blog.title || ''} ${blog.slug || ''} ${blog.excerpt || ''}`.toLowerCase();
        if (!hay.includes(lowerSearch)) return false;
      }

      const primary = getPrimaryDate(blog);
      if (fromTs || toTs) {
        if (!primary) return false;
        const p = new Date(primary).getTime();
        if (Number.isNaN(p)) return false;
        if (fromTs && p < fromTs) return false;
        if (toTs && p > toTs) return false;
      }
      return true;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'title') return (a.title || '').localeCompare(b.title || '');
      if (sortBy === 'oldest') return new Date(getPrimaryDate(a) || 0) - new Date(getPrimaryDate(b) || 0);
      return new Date(getPrimaryDate(b) || 0) - new Date(getPrimaryDate(a) || 0);
    });
  }, [blogs, searchTerm, sortBy, dateFrom, dateTo]);

  const groupedByMonth = useMemo(() => {
    return dashboardBlogs.reduce((acc, blog) => {
      const d = getPrimaryDate(blog);
      const key = d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'long' }) : 'No date';
      if (!acc[key]) acc[key] = [];
      acc[key].push(blog);
      return acc;
    }, {});
  }, [dashboardBlogs]);

  const totalBlogs = blogs.length;
  const publishedCount = blogs.filter((b) => b.status === 'published').length;
  const scheduledCount = blogs.filter((b) => b.status === 'scheduled').length;
  const draftCount = blogs.filter((b) => b.status === 'draft').length;
  const highlightCount = blogs.filter((b) => b.highlight).length;

  // Tactical hero level — editorial health
  const blogLevel = (() => {
    if (totalBlogs === 0) return 'warning';
    if (publishedCount === 0) return 'warning';
    if (draftCount > totalBlogs * 0.6) return 'warning';
    return 'good';
  })();
  const blogLabel = {
    good: 'PUBLISHING ACTIVE',
    warning: 'DRAFT BACKLOG',
    critical: 'NO LIVE POSTS',
  }[blogLevel];

  const publishedPct = totalBlogs ? (publishedCount / totalBlogs) * 100 : 0;
  const draftPct = totalBlogs ? (draftCount / totalBlogs) * 100 : 0;

  const blogStatusLevel = (s) => s === 'published' ? 'good' : s === 'scheduled' ? 'warning' : 'critical';
  const blogStatusLabel = (s) => (s || 'draft').toUpperCase();

  return (
    <div className="feedback-manager opo-tactical blog-tactical blog-manager-shell">
      {/* ─── Tactical hero + KPI strip ─────────────────────────── */}
      <div className="blog-tactical-top">
        <div className={`opo-hero opo-hero-${blogLevel}`}>
          <div className="opo-hero-scan" aria-hidden="true" />
          <div className="opo-hero-left">
            <div className="opo-hero-eyebrow">EDITORIAL OPERATIONS</div>
            <div className="opo-hero-title">
              <span className={`opo-hero-pulse opo-pulse-${blogLevel}`} />
              <h2>{blogLabel}</h2>
            </div>
            <div className="opo-hero-sub">
              {totalBlogs} post{totalBlogs === 1 ? '' : 's'} catalogued · {publishedCount} live · {scheduledCount} scheduled · {draftCount} draft{draftCount === 1 ? '' : 's'}
            </div>
          </div>
          <div className="opo-hero-right">
            <button
              type="button"
              className="opo-refresh-btn"
              onClick={() => { fetchBlogs(); fetchTiktokVideos(); }}
              title="Re-fetch blog catalog & TikTok videos"
            >
              <span className="opo-refresh-icon" /> SYNC
            </button>
            <button
              type="button"
              className="opo-refresh-btn blog-tactical-add-btn"
              onClick={openAdd}
              title="Compose a new blog post"
            >
              + NEW POST
            </button>
            <div className="opo-hero-timestamp">
              {new Date().toLocaleString()}
            </div>
          </div>
        </div>

        <div className="opo-kpi-row">
          <div className="opo-kpi">
            <div className="opo-kpi-label">TOTAL POSTS</div>
            <div className="opo-kpi-value opo-mono-value">{totalBlogs.toLocaleString()}</div>
            <div className="opo-kpi-foot">{dashboardBlogs.length} visible after filters</div>
          </div>
          <div className="opo-kpi">
            <div className="opo-kpi-label">PUBLISHED</div>
            <div className="opo-kpi-value opo-mono-value opo-level-good">{publishedCount}<span className="opo-kpi-sep">/</span>{totalBlogs}</div>
            <div className="opo-bar-track opo-bar-mini">
              <div className="opo-bar-fill opo-fill-good" style={{ width: `${publishedPct}%` }} />
            </div>
            <div className="opo-kpi-foot">{publishedPct.toFixed(0)}% of catalog live</div>
          </div>
          <div className="opo-kpi">
            <div className="opo-kpi-label">SCHEDULED</div>
            <div className="opo-kpi-value opo-mono-value opo-level-warning">{scheduledCount}</div>
            <div className="opo-kpi-foot">{draftCount} draft{draftCount === 1 ? '' : 's'} ({draftPct.toFixed(0)}%)</div>
          </div>
          <div className="opo-kpi">
            <div className="opo-kpi-label">HIGHLIGHTS</div>
            <div className="opo-kpi-value opo-mono-value">{highlightCount}</div>
            <div className="opo-kpi-foot">featured on optiprime.ai/blog</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="feedback-filters feedback-tactical-filters blog-tactical-filters">
        <span className="feedback-filters-tag">FILTERS</span>
        <label className="blog-manager-filter">
          <span>Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </label>
        <label className="blog-manager-filter">
          <span>Sort</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="title">Title A-Z</option>
          </select>
        </label>
        <label className="blog-manager-filter">
          <span>Date from</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="blog-manager-filter">
          <span>Date to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <label className="blog-manager-filter blog-manager-filter--search">
          <span>Search title / slug / excerpt</span>
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="e.g. richmond, optilaw, legal..." />
        </label>
      </div>

      {error && <div className="error-message">⚠️ {error}</div>}

      <div className="blog-tactical-list-header">
        <h3 className="opo-section-heading">
          <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
          POSTS
          <span className="blog-tactical-list-count opo-mono-value">{dashboardBlogs.length}</span>
        </h3>
        <span className="blog-tactical-list-meta">
          Grouped by month · {Object.keys(groupedByMonth).length} bucket{Object.keys(groupedByMonth).length === 1 ? '' : 's'}
        </span>
      </div>

      {loading ? (
        <div className="blog-tactical-empty">
          <i className="fas fa-spinner fa-spin" />
          <p>LOADING BLOGS…</p>
        </div>
      ) : dashboardBlogs.length === 0 ? (
        <div className="blog-tactical-empty">
          <i className="fas fa-file-alt" />
          <p>NO POSTS MATCH CURRENT FILTERS</p>
        </div>
      ) : (
        <div className="blog-manager-month-groups blog-tactical-month-groups">
          {Object.entries(groupedByMonth).map(([month, monthBlogs]) => (
            <section key={month} className="blog-manager-month-group blog-tactical-month-group">
              <header className="blog-manager-month-header blog-tactical-month-header">
                <h3 className="opo-section-heading">▮ {month.toUpperCase()}</h3>
                <span className="blog-tactical-month-count">
                  {monthBlogs.length} post{monthBlogs.length === 1 ? '' : 's'}
                </span>
              </header>
              <div className="feedback-list blog-tactical-cards">
                {monthBlogs.map((blog) => {
                  const lvl = blogStatusLevel(blog.status);
                  return (
                    <div key={blog.id} className={`feedback-item blog-manager-post-card blog-tactical-post-card blog-tactical-post-${lvl}`}>
                      <div className="blog-tactical-post-scan" aria-hidden="true" />
                      <div className="blog-manager-post-card__meta blog-tactical-post-meta">
                        <span className={`opo-pip opo-pip-${lvl}`}><span className="opo-pip-core" /></span>
                        <span className="blog-manager-post-date opo-mono-value">{formatDate(getPrimaryDate(blog))}</span>
                        <span className={`widgets-status-badge widgets-status-${lvl}`}>{blogStatusLabel(blog.status)}</span>
                        {blog.highlight && <span className="widgets-status-badge blog-tactical-highlight-badge">HIGHLIGHT</span>}
                      </div>
                      <h4 className="blog-tactical-post-title">{blog.title || blog.slug}</h4>
                      <div className="blog-manager-post-slug blog-tactical-post-slug">/{blog.slug}</div>
                      {blog.excerpt && (
                        <p className="blog-tactical-post-excerpt">
                          {blog.excerpt.length > 180 ? `${blog.excerpt.slice(0, 180)}…` : blog.excerpt}
                        </p>
                      )}
                      <div className="blog-manager-post-actions blog-tactical-post-actions">
                        <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" onClick={() => openView(blog)}>VIEW</button>
                        <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" onClick={() => openEdit(blog)}>EDIT</button>
                        <button type="button" className="blog-tactical-action-btn blog-tactical-action-delete" onClick={() => handleDelete(blog)}>DELETE</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* TikTok panel — tactical */}
      <div className="blog-tactical-tiktok-section">
        <h3 className="opo-section-heading">
          <span className="opo-pip opo-pip-warning"><span className="opo-pip-core" /></span>
          TIKTOK FEED
          <span className="blog-tactical-list-count opo-mono-value">{tiktokVideos.length}</span>
        </h3>
        <p className="blog-tactical-tiktok-desc">
          Paste a TikTok video URL or numeric id. Updates the <strong>Watch on TikTok</strong> section on optiprime.ai/blog.
        </p>
        <form onSubmit={handleAddTiktok} className="blog-tactical-tiktok-form">
          <input
            type="text"
            value={tiktokUrl}
            onChange={e => setTiktokUrl(e.target.value)}
            placeholder="https://www.tiktok.com/@user/video/1234567890123456789"
            className="blog-tactical-tiktok-input"
          />
          <button type="submit" className="blog-tactical-action-btn blog-tactical-action-edit" disabled={tiktokSubmitting}>
            {tiktokSubmitting ? 'ADDING…' : '+ ADD VIDEO'}
          </button>
          <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" onClick={fetchTiktokVideos}>RE-SYNC</button>
        </form>
        {tiktokError && <div className="error-message" style={{ marginBottom: '12px' }}>⚠️ {tiktokError}</div>}
        {tiktokLoading ? (
          <div className="blog-tactical-empty"><i className="fas fa-spinner fa-spin" /><p>LOADING TIKTOK LIST…</p></div>
        ) : tiktokVideos.length === 0 ? (
          <div className="blog-tactical-empty"><i className="fab fa-tiktok" /><p>NO TIKTOK VIDEOS YET</p></div>
        ) : (
          <div className="blog-tactical-tiktok-grid" aria-label="TikTok preview tiles (same embeds as public blog)">
            {tiktokVideos.map((v) => {
              const embedSrc = getTiktokEmbedSrc(v);
              return (
                <div key={v.id} className="blog-tactical-tiktok-tile">
                  <div className="blog-tactical-tiktok-frame">
                    {embedSrc ? (
                      <iframe
                        title={`TikTok @${v.tiktok_username || 'user'} — ${v.video_id}`}
                        src={embedSrc}
                        allow="fullscreen; autoplay"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                      />
                    ) : (
                      <div className="blog-tactical-tiktok-missing">Missing video id</div>
                    )}
                  </div>
                  <div className="blog-tactical-tiktok-tile-meta">
                    <span className="blog-tactical-tiktok-handle opo-mono-value">@{v.tiktok_username}</span>
                    <span className="blog-tactical-tiktok-order">ORDER · {v.sort_order}</span>
                  </div>
                  <div className="blog-tactical-tiktok-tile-actions">
                    {v.page_url && (
                      <a
                        href={v.page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="blog-tactical-tiktok-open"
                      >
                        OPEN ON TIKTOK
                      </a>
                    )}
                    <button
                      type="button"
                      className="blog-tactical-action-btn blog-tactical-action-delete"
                      onClick={() => handleDeleteTiktok(v.id)}
                    >
                      REMOVE
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit form modal */}
      {formOpen && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={closeForm}>
          <div className="modal-content" style={{ background: 'var(--surface-color)', borderRadius: '12px', padding: '24px', maxWidth: '560px', width: '90%', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>{editingBlog ? 'Edit blog' : 'Add blog'}</h2>
            {formError && <div className="error-message" style={{ marginBottom: '12px' }}>{formError}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Slug (URL) *</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={e => setFormData(d => ({ ...d, slug: e.target.value }))}
                  placeholder="e.g. my-new-post"
                  required
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--background)', color: 'var(--text-primary)' }}
                  disabled={!!editingBlog}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData(d => ({ ...d, title: e.target.value }))}
                  placeholder="Blog title"
                  required
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--background)', color: 'var(--text-primary)' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Excerpt</label>
                <textarea
                  value={formData.excerpt}
                  onChange={e => setFormData(d => ({ ...d, excerpt: e.target.value }))}
                  placeholder="Short summary"
                  rows={2}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--background)', color: 'var(--text-primary)' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Body</label>
                <RichTextEditor
                  content={formData.body}
                  onChange={html => setFormData(d => ({ ...d, body: html }))}
                  placeholder="Full post content (optional)"
                  onImageUpload={(insertImage) => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formDataUpload = new FormData();
                      formDataUpload.append('image', file);
                      try {
                        const res = await fetch(`${API_BLOG}/upload/image`, { method: 'POST', body: formDataUpload });
                        const data = await res.json();
                        if (data.success && data.url) {
                          insertImage(data.url);
                          setFormData(d => (d.image_url ? d : { ...d, image_url: data.url }));
                        } else alert(data.error || 'Upload failed');
                      } catch (err) {
                        alert(err.message || 'Upload failed');
                      }
                    };
                    input.click();
                  }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Image URL (thumbnail for cards / highlight reel)</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={e => setFormData(d => ({ ...d, image_url: e.target.value }))}
                    placeholder="https://..."
                    style={{ flex: '1', minWidth: '200px', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--background)', color: 'var(--text-primary)' }}
                  />
                  <button
                    type="button"
                    className="refresh-btn"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => {
                      const match = (formData.body || '').match(/<img[^>]+src=["']([^"']+)["']/);
                      if (match && match[1]) {
                        setFormData(d => ({ ...d, image_url: match[1] }));
                      } else {
                        alert('No image found in the body. Add an image in the post first, then click this button.');
                      }
                    }}
                  >
                    Use first image from body
                  </button>
                </div>
                <small style={{ color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                  Thumbnail is set automatically when you upload an image in the body (if this field is empty). Or use the button to copy the first body image URL here.
                </small>
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={e => setFormData(d => ({ ...d, status: e.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--background)', color: 'var(--text-primary)' }}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>
              {formData.status === 'scheduled' && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label>Scheduled for</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_for}
                    onChange={e => setFormData(d => ({ ...d, scheduled_for: e.target.value }))}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--background)', color: 'var(--text-primary)' }}
                  />
                </div>
              )}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ color: 'var(--accent-cyan)', marginBottom: '8px', display: 'block' }}>📋 SEO (optional)</label>
                <input
                  type="text"
                  value={formData.seo_meta.metaTitle}
                  onChange={e => setFormData(d => ({ ...d, seo_meta: { ...d.seo_meta, metaTitle: e.target.value } }))}
                  placeholder="Meta title"
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--background)', color: 'var(--text-primary)', marginBottom: '8px' }}
                />
                <textarea
                  value={formData.seo_meta.metaDescription}
                  onChange={e => setFormData(d => ({ ...d, seo_meta: { ...d.seo_meta, metaDescription: e.target.value } }))}
                  placeholder="Meta description"
                  rows={2}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--background)', color: 'var(--text-primary)', marginBottom: '8px' }}
                />
                <input
                  type="url"
                  value={formData.seo_meta.ogImage}
                  onChange={e => setFormData(d => ({ ...d, seo_meta: { ...d.seo_meta, ogImage: e.target.value } }))}
                  placeholder="OG image URL"
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--background)', color: 'var(--text-primary)' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="blog-highlight"
                  checked={formData.highlight}
                  onChange={e => setFormData(d => ({ ...d, highlight: e.target.checked }))}
                />
                <label htmlFor="blog-highlight">Show on highlight reel (optiprime.ai/blog)</label>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="refresh-btn" disabled={submitLoading}>
                  {submitLoading ? 'Saving…' : (editingBlog ? 'Update' : 'Create')}
                </button>
                <button type="button" className="refresh-btn" style={{ background: 'var(--text-secondary)' }} onClick={closeForm}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View modal */}
      {viewOpen && viewingBlog && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={closeView}>
          <div className="modal-content" style={{ background: 'var(--surface-color)', borderRadius: '12px', padding: '24px', maxWidth: '640px', width: '90%', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>Blog details</h2>
            <div style={{ marginBottom: '16px' }}>
              <strong>ID:</strong> {viewingBlog.id} &nbsp;|&nbsp; <strong>Slug:</strong> {viewingBlog.slug} &nbsp;|&nbsp;
              <span className="category-badge" style={{ backgroundColor: viewingBlog.status === 'published' ? '#22c55e' : viewingBlog.status === 'scheduled' ? '#f59e0b' : '#64748b' }}>{viewingBlog.status || 'published'}</span>
              {viewingBlog.highlight && <span className="category-badge" style={{ backgroundColor: 'var(--accent-cyan)', marginLeft: '4px' }}>Highlight</span>}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Title:</strong>
              <p style={{ margin: '4px 0 0 0' }}>{viewingBlog.title}</p>
            </div>
            {viewingBlog.excerpt && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Excerpt:</strong>
                <p style={{ margin: '4px 0 0 0', whiteSpace: 'pre-wrap' }}>{viewingBlog.excerpt}</p>
              </div>
            )}
            {viewingBlog.body && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Body:</strong>
                <div style={{ marginTop: '4px', maxHeight: '200px', overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: viewingBlog.body }} />
              </div>
            )}
            {viewingBlog.seo_meta && (viewingBlog.seo_meta.metaTitle || viewingBlog.seo_meta.metaDescription) && (
              <div style={{ marginBottom: '16px' }}>
                <strong>SEO:</strong>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem' }}>Meta: {viewingBlog.seo_meta.metaTitle || '—'} / {viewingBlog.seo_meta.metaDescription ? viewingBlog.seo_meta.metaDescription.slice(0, 80) + '…' : '—'}</p>
              </div>
            )}
            {viewingBlog.image_url && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Image URL:</strong>
                <p style={{ margin: '4px 0 0 0', wordBreak: 'break-all' }}>{viewingBlog.image_url}</p>
              </div>
            )}
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Uploaded: {formatDate(viewingBlog.uploaded_at)}
            </div>
            <div style={{ marginTop: '20px' }}>
              <button type="button" className="refresh-btn" onClick={closeView}>Close</button>
              <button type="button" className="refresh-btn" style={{ marginLeft: '8px', background: 'var(--primary-color)' }} onClick={() => { closeView(); openEdit(viewingBlog); }}>Edit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager;
